import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LincolnshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Lincolnshire Pothole Claims | 327,165 Potholes in Five Years | Fixtyer"
        description="Lincolnshire County Council filled 327,165 potholes in five years — 72,421 in 2024/25 alone — on one of England's largest road networks. Every DfT scorecard is AMBER, preventative spend is estimated at just 9-13%, and unclassified roads are surveyed once every four years. See what that means for your pothole claim."
        canonical="/councils/lincolnshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Lincolnshire', url: '/councils/lincolnshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Lincolnshire is AMBER on every DfT scorecard — what does that mean for my claim?',
            answer: 'The Department for Transport rated Lincolnshire AMBER for overall performance, road condition, spending and best practice in 2025/26 — with no GREEN anywhere. That does not win your claim by itself, but it is independent government evidence that the council\'s maintenance record is middling across the board. Combined with 72,421 potholes filled in 2024/25 and 23% of unclassified roads in RED condition, it gives you a documented backdrop against which to challenge any Section 58 defence.',
          },
          {
            question: 'What if my pothole was on an unclassified or rural road in Lincolnshire?',
            answer: 'Unclassified roads make up 4,134km — 46% of Lincolnshire\'s network — and 23% of them were in RED condition at the 2024 survey, roughly 950km of road. The council\'s own report states that condition surveys "are carried out on 25% of the Unclassified Network each year", meaning any given unclassified road is condition-surveyed only once every four years. That survey gap is a structural weakness in any Section 58 defence on those roads.',
          },
          {
            question: 'Lincolnshire spends nearly double its DfT allocation — doesn\'t that defeat my claim?',
            answer: 'No. In 2024/25 the council spent £83.3 million in capital against a £44.3 million DfT allocation, yet the DfT still rated its Spend scorecard AMBER — and the council\'s own estimates put preventative maintenance at just 9-13% of spending in every year since 2020/21. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate budgets.',
          },
          {
            question: 'Does filling 72,421 potholes in a year show the council is on top of the problem?',
            answer: 'It shows the opposite of a stable network. Pothole repairs have risen 44% in five years — from 50,422 in 2020/21 to 72,421 in 2024/25, with the last two years each setting a new record. That is roughly 198 potholes filled every single day. A network producing defects at that rate is one where new potholes routinely form between inspections — exactly the scenario where prior reports and photographic evidence decide claims.',
          },
          {
            question: 'What is the Lincolnshire Condition Indicator and why does it matter to my claim?',
            answer: 'The Lincolnshire Condition Indicator (LCI) is the council\'s own decision-making tool, which it says "consolidates data on claims, potholes, and public enquiries per kilometre". In other words, Lincolnshire formally tracks where damage claims cluster and feeds that into maintenance priorities. If claims and pothole reports were already concentrated on your road, that is data the council held — and knowledge of a problem it did not fix is central to defeating a Section 58 defence.',
          },
          {
            question: 'Unclassified RED roads improved from 28% to 23% — does that weaken my claim?',
            answer: 'Not meaningfully. Even after five years of improvement, almost a quarter of Lincolnshire\'s unclassified network was still in RED condition in 2024 — around 950km, not far short of the county\'s entire 1,090km A-road network. And because only a quarter of unclassified roads are surveyed each year, the figure for any individual road may be up to four years out of date.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Lincolnshire County Council" 
        councilSlug="lincolnshire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/lincolnshire"
        headline="Lincolnshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Lincolnshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Lincolnshire: AMBER on Every Scorecard, 72,421 Potholes in a Year
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Lincolnshire County Council manages one of the largest local road networks in England — 
            8,922km — and the DfT rated it <strong className="text-amber-400">AMBER on all four scorecards</strong> for 
            2025/26: overall, condition, spend and best practice. Not a single GREEN. Pothole repairs are up 
            <strong className="text-rose-400"> 44% in five years</strong>, preventative maintenance is estimated 
            at just 9-13% of spending, and unclassified roads — 46% of the network — are condition-surveyed 
            only once every four years.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">72,421</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Roughly 198 potholes every single day — a new record for the second year running, 
              and up 44% on the 50,422 filled in 2020/21.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Lincolnshire's own transparency report — 
            A-roads holding but slipping, B and C roads stuck, and nearly a quarter of unclassified roads in RED
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (1,090km — 12% of network): good, but slipping</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 2% in every prior year</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">19%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">78%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 80% in 2022</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: Lincolnshire's A-roads are in better shape than the rural authority average, 
              and the council says so. But 2024 is the first year RED has risen in this dataset — from 2% to 3% — 
              and GREEN has fallen two points since 2022. The direction of travel just turned.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (3,697km — 41% of network): five years of no improvement</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">23%</td>
                    <td className="px-4 py-2 text-center font-bold">71%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B and C roads have sat at 6% for five consecutive years — roughly 222km of road 
              that "should be considered for maintenance" under the SCANNER definition, on the road class that 
              makes up the biggest share of Lincolnshire's network. Five years of investment has not moved 
              that needle at all.
            </p>
          </div>

          {/* Unclassified */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (4,134km — 46% of network): nearly a quarter in RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Unclassified roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">28%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">27%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">26%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">26%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Improving, yes — but 23% of 4,134km is still roughly 950km of unclassified road in RED condition. 
              That is not far short of Lincolnshire's entire 1,090km A-road network, in the worst SCANNER 
              category, on the rural lanes and residential streets where most drivers actually hit potholes.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And Spend Is AMBER Too</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£44.3m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£83.3m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">9%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Lincolnshire spent nearly <span className="text-amber-400">double its DfT allocation</span> in 
              2024/25 — and the DfT still rated its Spend scorecard AMBER. Unlike GREEN-spend counties such as 
              Buckinghamshire, which estimates over 80% of its spend as preventative, Lincolnshire's own report 
              puts preventative maintenance at just 9-13% of spending in every year since 2020/21.
            </p>
          </div>
        </div>
      </section>

      {/* The Four-Year Survey Cycle */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Four-Year Survey Cycle
          </h2>
          <p className="text-surface-600 mb-8">
            46% of the network is unclassified roads — and only a quarter of them are condition-surveyed each year
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="CVI surveys are carried out on 25% of the Unclassified Network each year."
              source="Lincolnshire County Council Local Highways Maintenance Transparency Report"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What 25% a Year Actually Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                If only a quarter of the 4,134km unclassified network is condition-surveyed annually, any 
                given unclassified road gets a Coarse Visual Inspection roughly <strong>once every four 
                years</strong>. The condition data the council holds for your road could be up to four 
                years out of date by the time you hit the pothole.
              </p>
              <p className="text-surface-600 text-sm">
                Compare the A-roads, where the report says SCANNER and CVI surveys cover the whole network 
                "in a single direction each year". The roads in the best condition get annual surveys; the 
                roads with 23% in RED get quadrennial ones.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Scale of the Known Problem</h3>
              <p className="text-amber-800 text-sm mb-3">
                At the 2024 survey, 23% of unclassified roads were in RED condition — approximately 950km. 
                The council's own asset management strategy commits only to "gradually improve the condition 
                of the unclassified road network" while maintaining "a steady state across all highway asset 
                groups" elsewhere.
              </p>
              <p className="text-amber-800 text-sm">
                That is a documented, published acknowledgement that a large share of the network is in poor 
                condition and the plan is gradual improvement — not elimination of the backlog.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing 
              the condition of its roads and acting on it. For Lincolnshire's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — this year, or three years ago?</li>
              <li>• If 23-28% of unclassified roads were RED at every survey since 2020, what was done about yours?</li>
              <li>• How does the council track deterioration on a road it inspects for condition once every four years?</li>
              <li>• Did the safety inspection regime for your road's hierarchy actually catch the defect that damaged you?</li>
            </ul>
            <p className="text-white font-medium">
              A council managing one of England's largest networks cannot claim detailed knowledge of roads 
              it condition-surveys once every four years — while its own data shows nearly a quarter of them 
              in the worst category.
            </p>
          </div>
        </div>
      </section>

      {/* The Pothole Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            327,165 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own estimates — and the trend is going the wrong way
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Per day</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">50,422</td>
                  <td className="px-6 py-4 text-center">~138</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">67,143</td>
                  <td className="px-6 py-4 text-center">~184</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">66,909</td>
                  <td className="px-6 py-4 text-center">~183</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">70,270</td>
                  <td className="px-6 py-4 text-center">~193</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">72,421</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~198</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">327,165</td>
                  <td className="px-6 py-4 text-center font-bold">~179</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Two Record Years in a Row</h3>
              <p className="text-surface-600 text-sm">
                Pothole repairs are up 44% since 2020/21, and both 2023/24 and 2024/25 set new records. 
                A network that needs 198 potholes filled every day is, by definition, a network where 
                defects form faster than planned maintenance can prevent them — exactly the scenario where 
                a pothole can open up, damage your vehicle and only then get repaired.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">85% of Reactive Spend Goes on Potholes</h3>
              <p className="text-amber-800 text-sm">
                The council's own report states: "Approximately 85% of reactive spend is allocated to 
                pothole repairs." Reactive work is firefighting — fixing defects after they appear, often 
                after they have been reported or have already caused damage. When the great majority of 
                your reactive budget is potholes, potholes are your defining problem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Where The Money Goes */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Where The Money Goes — And Why Spend Is AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Big budgets, big overspend against allocation — and a preventative share the council itself estimates at 9-13%
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">Est. preventative</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">Est. reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono text-brand-900">2020/21</td>
                  <td className="px-4 py-3 text-center">£51.061m</td>
                  <td className="px-4 py-3 text-center">£51.061m</td>
                  <td className="px-4 py-3 text-center">£19.732m</td>
                  <td className="px-4 py-3 text-center">11%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono text-brand-900">2021/22</td>
                  <td className="px-4 py-3 text-center">£38.973m</td>
                  <td className="px-4 py-3 text-center">£51.273m</td>
                  <td className="px-4 py-3 text-center">£19.732m</td>
                  <td className="px-4 py-3 text-center">12%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono text-brand-900">2022/23</td>
                  <td className="px-4 py-3 text-center">£38.793m</td>
                  <td className="px-4 py-3 text-center">£61.799m</td>
                  <td className="px-4 py-3 text-center">£20.937m</td>
                  <td className="px-4 py-3 text-center">13%</td>
                  <td className="px-4 py-3 text-center">8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono text-brand-900">2023/24</td>
                  <td className="px-4 py-3 text-center">£45.607m</td>
                  <td className="px-4 py-3 text-center">£71.767m</td>
                  <td className="px-4 py-3 text-center">£30.677m</td>
                  <td className="px-4 py-3 text-center">11%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono text-brand-900">2024/25</td>
                  <td className="px-4 py-3 text-center">£44.275m</td>
                  <td className="px-4 py-3 text-center font-bold">£83.336m</td>
                  <td className="px-4 py-3 text-center">£32.468m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">9%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
                <tr className="bg-surface-50">
                  <td className="px-4 py-3 font-mono font-bold text-brand-900">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£60.442m</td>
                  <td className="px-4 py-3 text-center font-bold">£73.798m</td>
                  <td className="px-4 py-3 text-center font-bold">£32.812m</td>
                  <td className="px-4 py-3 text-center font-bold">12%</td>
                  <td className="px-4 py-3 text-center font-bold">12%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-surface-500 mb-8">
            Preventative and reactive percentages are the council's own estimates as published; they do not 
            sum to 100% of spending, with the remainder covering other highway activities.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Preventative Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                The report's own strategy section says Lincolnshire "prioritises a shift from reactive, 
                short-term fixes to a more sustainable and cost-effective preventative maintenance approach" — 
                yet in 2024/25 the estimated preventative share fell to 9%, its lowest in the published series.
              </p>
              <p className="text-surface-600 text-sm">
                The council adds that it is "actively working to increase the proportion of spend on 
                preventative maintenance". That is a stated ambition, not an achievement — and the AMBER 
                Spend scorecard reflects it.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Resurfacing Plans Are Shrinking</h3>
              <p className="text-amber-800 text-sm mb-3">
                In 2024/25 the council delivered 240 carriageway resurfacing schemes covering 186.8km. 
                For 2025/26 it plans 226 schemes covering just 120.2km — a cut of more than a third in 
                resurfaced length, on a network where potholes hit a record 72,421 last year.
              </p>
              <p className="text-amber-800 text-sm">
                Surface dressing coverage is also planned to fall, from 280km delivered to 261km planned.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Lincolnshire's principal road network is currently in better condition than the average for rural authorities. The condition of unclassified roads has also improved significantly in recent years."
              source="Lincolnshire County Council Local Highways Maintenance Transparency Report"
            />
            <QuoteCard 
              quote="The Lincolnshire Condition Indicator (LCI) consolidates data on claims, potholes, and public enquiries per kilometre to inform decision-making."
              source="Lincolnshire County Council Local Highways Maintenance Transparency Report"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Council Tracks Claims Per Kilometre</h3>
            <p className="text-brand-200 mb-4">
              That second quote matters more than it first appears. Lincolnshire's own decision-making tool 
              explicitly consolidates <strong className="text-white">damage claims, pothole reports and public 
              enquiries per kilometre</strong> of road. In plain terms:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• The council holds data on exactly where claims and pothole reports cluster</li>
              <li>• If your road already had a history of claims or reports, the council knew about it</li>
              <li>• Knowledge of a recurring problem that was not fixed is central to defeating Section 58</li>
              <li>• A subject access or FOI request for LCI data on your road can be revealing</li>
            </ul>
            <p className="text-white font-medium">
              When a council's own systems flag a kilometre of road as a claims hotspot, "we couldn't 
              reasonably have known" becomes a very hard argument to run.
            </p>
          </div>
        </div>
      </section>

      {/* Claiming against an all-AMBER council */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an All-AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Lincolnshire is a serious, data-driven highways authority — and its own data still helps you
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Spends well above its DfT allocation — £83.3m against £44.3m in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads in better condition than the rural authority average, surveyed annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Unclassified RED improving — 28% down to 23% over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented systems: CONFIRM asset management, LCI, risk-based inspections, AI-assisted surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Big delivery: 240 resurfacing and 231 surface dressing schemes in 2024/25
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a professional, well-documented Section 58 defence. Generic template claims will struggle here.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four DfT scorecards — no GREEN anywhere, unlike most comparable counties
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 72,421 potholes filled in 2024/25 — a record, up 44% in five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative spend estimated at just 9-13% — the firefighting model, by the council's own numbers
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 23% of unclassified roads in RED — roughly 950km — surveyed only once every four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C roads stuck at 6% RED for five straight years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2025/26 resurfacing plans cut by over a third in length versus 2024/25 delivery
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a data-driven council, beat them with their own data. Your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on an unclassified road, the four-year survey cycle is your strongest structural argument</li>
              <li>• The LCI angle — request the claims and enquiry history the council holds for your road</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Lincolnshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Lincolnshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            One of England's largest networks demands a properly built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No LCI claims-history angle</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ All-AMBER DfT scorecards cited</li>
                <li>✅ 327,165 potholes in five years documented</li>
                <li>✅ Four-year unclassified survey gap argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Lincolnshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=lincolnshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Lincolnshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Lincolnshire is AMBER on every DfT scorecard — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The Department for Transport rated Lincolnshire AMBER for overall performance, road condition, 
                spending and best practice in 2025/26 — with no GREEN anywhere. That does not win your claim 
                by itself, but it is independent government evidence that the council's maintenance record is 
                middling across the board. Combined with 72,421 potholes filled in 2024/25 and 23% of 
                unclassified roads in RED condition, it gives you a documented backdrop against which to 
                challenge any Section 58 defence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or rural road in Lincolnshire?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 4,134km — 46% of Lincolnshire's network — and 23% of them were in 
                RED condition at the 2024 survey, roughly 950km of road. The council's own report states that 
                condition surveys "are carried out on 25% of the Unclassified Network each year", meaning any 
                given unclassified road is condition-surveyed only once every four years. That survey gap is 
                a structural weakness in any Section 58 defence on those roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Lincolnshire spends nearly double its DfT allocation — doesn't that defeat my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. In 2024/25 the council spent £83.3 million in capital against a £44.3 million DfT 
                allocation, yet the DfT still rated its Spend scorecard AMBER — and the council's own 
                estimates put preventative maintenance at just 9-13% of spending in every year since 2020/21. 
                Section 58 turns on whether the specific defect that damaged your vehicle was reasonably 
                inspected and repaired, not on aggregate budgets.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does filling 72,421 potholes in a year show the council is on top of the problem?
              </h3>
              <p className="text-surface-600 text-sm">
                It shows the opposite of a stable network. Pothole repairs have risen 44% in five years — 
                from 50,422 in 2020/21 to 72,421 in 2024/25, with the last two years each setting a new 
                record. That is roughly 198 potholes filled every single day. A network producing defects at 
                that rate is one where new potholes routinely form between inspections — exactly the scenario 
                where prior reports and photographic evidence decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What is the Lincolnshire Condition Indicator and why does it matter to my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The Lincolnshire Condition Indicator (LCI) is the council's own decision-making tool, which it 
                says "consolidates data on claims, potholes, and public enquiries per kilometre". In other 
                words, Lincolnshire formally tracks where damage claims cluster and feeds that into maintenance 
                priorities. If claims and pothole reports were already concentrated on your road, that is data 
                the council held — and knowledge of a problem it did not fix is central to defeating a 
                Section 58 defence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Unclassified RED roads improved from 28% to 23% — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not meaningfully. Even after five years of improvement, almost a quarter of Lincolnshire's 
                unclassified network was still in RED condition in 2024 — around 950km, not far short of the 
                county's entire 1,090km A-road network. And because only a quarter of unclassified roads are 
                surveyed each year, the figure for any individual road may be up to four years out of date.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.lincolnshire.gov.uk/maintenance/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Lincolnshire County Council Local Highways Maintenance Transparency Report
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
